import { UploaderFile } from "#root/components/ImageUploader";

export const toTitleCase = (str: any) => {
  return str.toLowerCase().split(' ').map((word: any) => {
    return toSentenceCase(word);
  }).join(' ');
};

export const toSentenceCase = (str: any) => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const formatLongDate = (dateString: string | Date | undefined) => {
  if (!dateString) return '-';
  
  return formatToLocalTime(dateString, 'long');
};

// American date format
export const toNormalDate = (date: string | number, isShort = false) => {
  const d = new Date(date);
  const day = d.getDate() < 10 ? `0${d.getDate()}` : d.getDate();
  const month =
    d.getMonth() + 1 < 10 ? `0${d.getMonth() + 1}` : d.getMonth() + 1;
  const year = d.getFullYear();

  if (isShort) {
    return `${month}.${day}.${year}`;
  }

  const hours = d.getHours();
  const minutes = d.getMinutes();
  const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;

  return `${month}.${day}.${year} - ${hours}:${formattedMinutes}pm`;
};

export const formatShortDate = (date: Date | string | undefined): string => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
    });
};

export function getMimeType(url: string): string {
  const extension = url.split('.').pop()?.toLowerCase();
  if (extension === 'png') return 'image/png';
  return 'image/jpeg';
}

const MIME_TYPES = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  bmp: 'image/bmp'
} as const;

type SupportedExtension = keyof typeof MIME_TYPES;

export async function urlToFile(url: string, id?: string): Promise<UploaderFile> {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.statusText}`);
    }

    const blob = await response.blob();
    const extension = (url.split(".").pop()?.toLowerCase() || "jpg") as SupportedExtension;
    const mimeType = MIME_TYPES[extension] || MIME_TYPES.jpg;
    const filename = `photo.${extension}`;

    // Create a new File object with the correct type
    const file = new File([blob], filename, { type: mimeType });

    // Convert to MediaFile by adding the id property
    const mediaFile = Object.assign(file, { id }) as UploaderFile;

    return mediaFile;
  } catch (error) {
    console.error('Error converting URL to file:', error);
    throw error;
  }
}

export const fileToBase64 = (file: File): Promise<string> => {
    return new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
            const base64 = reader.result as string;
            resolve(base64.split(',')[1]); // Remove data URL prefix
        };
        reader.readAsDataURL(file);
    });
};

export function downloadReportDocx(data: Blob): void {
  const blob = new Blob([data], {
    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', 'report.docx');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export function downloadBlob(blob: Blob, fileName: string) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  window.URL.revokeObjectURL(url);
}

/**
 * Handles date selection with timezone adjustment to prevent date shifting issues.
 * Sets the time portion of the date to noon (12:00:00) to provide a buffer against timezone conversions.
 * 
 * @param date - The selected date from a DatePicker
 * @returns A new Date object with time set to noon
 */
export const adjustDateTimezone = (date: Date | null | undefined): Date | null | undefined => {
    if (!date) return date;
    
    const adjustedDate = new Date(date);
    adjustedDate.setHours(12, 0, 0, 0);
    return adjustedDate;
};

/**
 * Utility function for DatePicker components to handle date selection with timezone adjustment.
 * Use this with DatePicker's onSelectDate prop to prevent date shifting issues.
 * 
 * @param fieldName - The name of the field to update
 * @param date - The selected date from the DatePicker
 * @param onFieldChange - The callback function to update the field value
 */
export const handleDateSelection = (
    fieldName: string, 
    date: Date | null | undefined, 
    onFieldChange: (name: string, value: any) => void
): void => {
    onFieldChange(fieldName, adjustDateTimezone(date));
};

/**
 * Formats a time value to a human-readable format like "8h 30m"
 * @param totalTime Time value as string in format "d.hh:mm:ss" or number in milliseconds
 * @returns Formatted time string
 */
export function formatTimeSpan(totalTime: string | number | undefined): string {
  if (totalTime === undefined || totalTime === null) return '-';
  
  let hours = 0;
  let minutes = 0;
  
  if (typeof totalTime === 'string') {
    // Parse the TimeSpan string format "d.hh:mm:ss.fffffff" or "hh:mm:ss.fffffff"
    try {
      // Handle different TimeSpan formats
      let timeString = totalTime;
      let days = 0;
      
      // Check if there's a day component (contains a dot)
      if (totalTime.includes('.')) {
        const parts = totalTime.split('.');
        // If the format is d.hh:mm:ss
        if (parts[0].includes(':')) {
          // No days, just a decimal in seconds
          timeString = totalTime;
        } else {
          // Has days
          days = parseInt(parts[0], 10);
          timeString = parts[1];
        }
      }
      
      // Parse the time part (hh:mm:ss)
      const timeParts = timeString.split(':');
      if (timeParts.length >= 2) {
        // Add days * 24 to hours
        hours = (days * 24) + parseInt(timeParts[0], 10);
        minutes = parseInt(timeParts[1], 10);
        
        // Round up minutes if seconds are 30 or more
        if (timeParts.length > 2) {
          const secondsPart = timeParts[2].split('.')[0]; // Remove fractional seconds
          const seconds = parseInt(secondsPart, 10);
          if (seconds >= 30) {
            minutes += 1;
            if (minutes === 60) {
              hours += 1;
              minutes = 0;
            }
          }
        }
      }
    } catch (e) {
      console.error('Error parsing time span:', e);
      return '-';
    }
  } else {
    // Convert milliseconds to hours and minutes
    hours = Math.floor(totalTime / (1000 * 60 * 60));
    minutes = Math.floor((totalTime % (1000 * 60 * 60)) / (1000 * 60));
  }
  
  // Format as "Xh Ym"
  if (hours === 0 && minutes === 0) {
    return `0m`;
  }
  
  if (hours === 0) {
    return `${minutes}m`;
  }
  
  return `${hours}h ${minutes}m`;
}

/**
 * Formats a mileage value to a human-readable format like "80mi"
 * @param mileage Mileage value
 * @returns Formatted mileage string
 */
export function formatMileage(mileage: number | undefined): string {
  if (mileage === undefined || mileage === null) return '-';
  
  // Round to nearest whole number and format as "Xmi"
  if (mileage === 0) {
    return `0mi`;
  }
  
  return `${Math.round(mileage)}mi`;
}

/**
 * Formats a date to MM/DD/YYYY hhmm format (military time) in the specified timezone
 * @param dateString Date string or Date object
 * @param timezone IANA timezone string (e.g., 'America/New_York' for Eastern Time)
 * @param isUtc Whether the input dateString should be treated as UTC (default: true)
 * @returns Formatted date string in MM/DD/YYYY hhmm format in the specified timezone
 */
export function formatDateTimeMMDDYYYYhhmm(
  dateString: string | Date | undefined, 
  timezone: string = 'America/New_York',
  isUtc: boolean = true
): string {
  if (!dateString) return '-';
  
  try {
    let date: Date;
    
    // Handle the date creation based on whether it's UTC or not
    if (isUtc && typeof dateString === 'string') {
      // If it's a string and should be treated as UTC, ensure it's parsed as UTC
      // by appending 'Z' if it doesn't already have timezone info
      if (!dateString.endsWith('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
        date = new Date(dateString + 'Z');
      } else {
        date = new Date(dateString);
      }
    } else {
      // Otherwise, just create a date object normally
      date = new Date(dateString);
    }
    
    if (isNaN(date.getTime())) {
      return '-';
    }
    
    // Use Intl.DateTimeFormat to format the date in the specified timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    
    // Format the date
    const formattedDate = formatter.format(date);
    
    // The format will be MM/DD/YYYY, HH:MM AM/PM, so we need to transform it
    // Example: "05/12/2025, 10:30 AM" -> "05/12/2025 10:30 AM"
    const [datePart, timePart] = formattedDate.split(', ');
    
    return `${datePart} ${timePart}`;
  } catch (e) {
    console.error('Error formatting date:', e);
    return '-';
  }
}

/**
 * Formats a UTC datetime to the user's local timezone
 * @param dateString Date string or Date object in UTC
 * @param format The format to use: 'short' (MM/DD/YYYY HH:MM), 'long' (Month DD, YYYY, HH:MM), or 'date' (MM/DD/YYYY)
 * @returns Formatted date string in the user's local timezone
 */
export function formatUtcToLocal(
  dateString: string | Date | undefined,
  format: 'short' | 'long' | 'date' = 'short'
): string {
  if (!dateString) return '-';
  
  try {
    let date: Date;
    
    // Ensure the date is treated as UTC
    if (typeof dateString === 'string') {
      // Append 'Z' if it doesn't already have timezone info to ensure UTC interpretation
      if (!dateString.endsWith('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
        date = new Date(dateString + 'Z');
      } else {
        date = new Date(dateString);
      }
    } else {
      date = new Date(dateString);
    }
    
    if (isNaN(date.getTime())) {
      return '-';
    }
    
    // Format options based on the requested format
    let options: Intl.DateTimeFormatOptions;
    
    switch (format) {
      case 'long':
        options = {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        };
        break;
      case 'date':
        options = {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        };
        break;
      case 'short':
      default:
        options = {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        };
        break;
    }
    
    // Format the date using the browser's locale and timezone settings
    const formattedDate = new Intl.DateTimeFormat('en-US', options).format(date);
    
    return formattedDate;
  } catch (e) {
    console.error('Error formatting date:', e);
    return '-';
  }
}

export function formatToLocalTime(
  dateString: string | Date | undefined,
  format: 'short' | 'long' | 'date' | 'time' = 'short',
  forceUtc: boolean = true
): string {
  if (!dateString) return '-';
  
  try {
    let date: Date;
    
    if (forceUtc && typeof dateString === 'string') {
      if (!dateString.endsWith('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
        date = new Date(dateString + 'Z');
      } else {
        date = new Date(dateString);
      }
    } else {
      date = new Date(dateString);
    }
    
    if (isNaN(date.getTime())) {
      return '-';
    }
    
    let options: Intl.DateTimeFormatOptions;
    
    switch (format) {
      case 'long':
        options = {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        };
        break;
      case 'date':
        options = {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        };
        break;
      case 'time':
        options = {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        };
        break;
      case 'short':
      default:
        options = {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        };
        break;
    }
    
    return new Intl.DateTimeFormat('en-US', options).format(date);
  } catch (e) {
    console.error('Error formatting date:', e);
    return '-';
  }
}

export function createLocalDate(
  value?: string | Date | null,
  forceUtc: boolean = true
): Date | undefined {
  if (!value) return undefined;
  
  try {
    let date: Date;
    
    if (forceUtc && typeof value === 'string') {
      if (!value.endsWith('Z') && !value.includes('+') && !value.includes('-', 10)) {
        date = new Date(value + 'Z');
      } else {
        date = new Date(value);
      }
    } else {
      date = new Date(value);
    }
    
    if (isNaN(date.getTime())) {
      return undefined;
    }
    
    return date;
  } catch (e) {
    console.error('Error creating local date:', e);
    return undefined;
  }
}

/**
 * Gets the user's timezone in a format that can be used for server-side timezone conversion
 * Maps IANA timezone names to Windows timezone names or returns original timezone if no mapping exists
 * @returns Timezone string suitable for server-side use
 */
export const getUserTimeZone = (): string => {
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    const timeZoneAbbreviations: Record<string, string> = {
      "America/New_York": "Eastern Standard Time",
      "America/Chicago": "Central Standard Time",
      "America/Denver": "Mountain Standard Time",
      "America/Los_Angeles": "Pacific Standard Time",
      "America/Phoenix": "US Mountain Standard Time",
      "America/Anchorage": "Alaskan Standard Time",
      "America/Honolulu": "Hawaiian Standard Time",
      "Europe/London": "GMT Standard Time",
      "Europe/Berlin": "Central European Standard Time",
      "Europe/Moscow": "Russian Standard Time",
      "Asia/Tokyo": "Tokyo Standard Time",
      "Asia/Shanghai": "China Standard Time",
      "Australia/Sydney": "AUS Eastern Standard Time",
      "Pacific/Auckland": "New Zealand Standard Time"
    };
    
    return timeZoneAbbreviations[timeZone] || timeZone || "Eastern Standard Time";
  } catch {
    return "Eastern Standard Time";
  }
};

/**
 * Gets timezone abbreviation for display purposes (EST, CST, etc.)
 * @returns Short timezone abbreviation
 */
export const getTimeZoneAbbreviation = (): string => {
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    const timeZoneShortNames: Record<string, string> = {
      "America/New_York": "EST",
      "America/Chicago": "CST",
      "America/Denver": "MST",
      "America/Los_Angeles": "PST",
      "America/Phoenix": "MST",
      "America/Anchorage": "AKST",
      "America/Honolulu": "HST",
      "Europe/London": "GMT",
      "Europe/Berlin": "CET",
      "Europe/Moscow": "MSK",
      "Asia/Tokyo": "JST",
      "Asia/Shanghai": "CST",
      "Australia/Sydney": "AEST",
      "Pacific/Auckland": "NZST"
    };
    
    return timeZoneShortNames[timeZone] || 
           (timeZone?.split("/").pop() || "Local");
  } catch {
    return "EST";
  }
};

